import React, { useState, useEffect, useRef } from 'react';
import { usersApi, profilesApi } from '../api';
import { toast } from 'sonner';
import { extractApiError } from '../utils/dateValidation';
import {
  Users as UsersIcon,
  Plus,
  Search,
  Edit,
  Trash2,
  Ban,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
} from 'lucide-react';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    department: '',
    position: '',
    phone: '',
    profile_id: '',
    is_active: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, profilesRes] = await Promise.all([
        usersApi.list(),
        profilesApi.list(),
      ]);
      setUsers(usersRes.data);
      setProfiles(profilesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (user = null) => {
    if (user) {
      try {
        const res = await usersApi.get(user.id);
        const fullUser = res.data;
        setEditingUser(fullUser);
        setFormData({
          email: fullUser.email,
          password: '',
          full_name: fullUser.full_name,
          department: fullUser.department || '',
          position: fullUser.position || '',
          phone: fullUser.phone || '',
          profile_id: fullUser.profile_id || '',
          is_active: fullUser.is_active,
        });
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados do usuário');
      }
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        department: '',
        position: '',
        phone: '',
        profile_id: '',
        is_active: true,
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      department: '',
      position: '',
      phone: '',
      profile_id: '',
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard against double submission (e.g. double-click or Enter + click firing
    // before React re-renders the disabled button): setSaving() is async, so a
    // second submit can slip through the state update. A ref blocks it synchronously.
    if (submittingRef.current) return;

    if (!formData.email || !formData.full_name) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Senha é obrigatória para novos usuários');
      return;
    }

    submittingRef.current = true;
    setSaving(true);

    try {
      const data = { ...formData };
      if (!data.password) delete data.password;
      if (!data.profile_id) delete data.profile_id;
      
      if (editingUser) {
        await usersApi.update(editingUser.id, data);
        toast.success('Alteração realizada com sucesso.');
      } else {
        await usersApi.create(data);
        toast.success('Usuário criado com sucesso.');
      }
      
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar usuário'));
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  const handleDeactivate = async (user) => {
    const action = user.is_active ? 'desativar' : 'reativar';
    if (!window.confirm(`Deseja realmente ${action} o usuário "${user.full_name}"?`)) {
      return;
    }

    try {
      if (user.is_active) {
        await usersApi.delete(user.id);
      } else {
        await usersApi.update(user.id, { is_active: true });
      }
      toast.success(`Usuário ${user.is_active ? 'desativado' : 'reativado'} com sucesso!`);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, `Erro ao ${action} usuário`));
    }
  };

  const handlePermanentDelete = async (user) => {
    if (!window.confirm(
      `Excluir permanentemente o usuário "${user.full_name}"? Esta ação não pode ser desfeita. ` +
      `Se ele tiver registros vinculados no sistema, a exclusão será bloqueada e você deverá desativá-lo em vez disso.`
    )) {
      return;
    }

    try {
      await usersApi.permanentDelete(user.id);
      toast.success('Usuário excluído permanentemente!');
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao excluir usuário'));
    }
  };

  const filteredUsers = users.filter(user =>
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <UsersIcon className="text-green-500" />
            Usuários
          </h1>
          <p className="text-gray-400 mt-1">Gerenciar usuários do sistema</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-user-btn"
        >
          <Plus size={20} />
          Novo Usuário
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Buscar usuários..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input pl-10"
          data-testid="search-users"
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Departamento</th>
                  <th>Cargo</th>
                  <th>Perfil</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">
                      Nenhum usuário encontrado
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="font-medium text-white">{user.full_name}</td>
                      <td>{user.email}</td>
                      <td>{user.department || '-'}</td>
                      <td>{user.position || '-'}</td>
                      <td>
                        <span className="badge badge-blue">
                          {user.profile_name || 'Sem perfil'}
                        </span>
                      </td>
                      <td>
                        {user.is_active ? (
                          <span className="badge badge-green flex items-center gap-1 w-fit">
                            <CheckCircle size={14} />
                            Ativo
                          </span>
                        ) : (
                          <span className="badge badge-red flex items-center gap-1 w-fit">
                            <XCircle size={14} />
                            Inativo
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenModal(user)}
                            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeactivate(user)}
                            className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-lg transition-colors"
                            title={user.is_active ? 'Desativar' : 'Reativar'}
                          >
                            {user.is_active ? <Ban size={18} /> : <CheckCircle size={18} />}
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(user)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                            title="Excluir permanentemente"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="input"
                    placeholder="Nome do usuário"
                    data-testid="user-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    placeholder="email@empresa.com"
                    data-testid="user-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {editingUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input pr-10"
                      placeholder="••••••••"
                      data-testid="user-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Perfil de Acesso
                  </label>
                  <select
                    value={formData.profile_id}
                    onChange={(e) => setFormData({ ...formData, profile_id: e.target.value })}
                    className="input"
                    data-testid="user-profile-select"
                  >
                    <option value="">Selecione um perfil</option>
                    {profiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Departamento
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="input"
                    placeholder="Ex: Qualidade"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Cargo
                  </label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="input"
                    placeholder="Ex: Analista de Qualidade"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Telefone
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-300">Usuário Ativo</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary flex items-center gap-2"
                  disabled={saving}
                  data-testid="save-user-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar'
                  )}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default Users;
